/**
 * FeaturedLocations Component
 *
 * Grid of popular Lebanese destinations with live property counts.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { Container } from '../../../components/layout';

interface Location {
  name: string;
  slug: string;
  image: string;
  propertyCount: number;
  description: string;
}

const FEATURED_LOCATIONS_BASE: Omit<Location, 'propertyCount'>[] = [
  {
    name: 'Batroun',
    slug: 'batroun',
    image: 'https://storage.googleapis.com/lebanest-e1a43.firebasestorage.app/locations/Batroun.webp',
    description: 'Beach bars, Phoenician ruins, lemonade',
  },
  {
    name: 'Ehden',
    slug: 'ehden',
    image: 'https://storage.googleapis.com/lebanest-e1a43.firebasestorage.app/locations/Ehden.jpg',
    description: 'Cedars, hiking trails, cool summers',
  },
  {
    name: 'Faraya',
    slug: 'faraya',
    image: 'https://storage.googleapis.com/lebanest-e1a43.firebasestorage.app/locations/Faraya.jpg',
    description: 'Ski slopes in winter, hikes the rest of the year',
  },
  {
    name: 'Byblos',
    slug: 'byblos',
    image: 'https://storage.googleapis.com/lebanest-e1a43.firebasestorage.app/locations/Byblos.webp',
    description: 'Old harbour, stone alleys, seafood',
  },
  {
    name: 'Beirut',
    slug: 'beirut',
    image: 'https://storage.googleapis.com/lebanest-e1a43.firebasestorage.app/locations/Beirut.jpg',
    description: 'The capital. Bars stay open late.',
  },
  {
    name: 'Tannourine',
    slug: 'tannourine',
    image: 'https://storage.googleapis.com/lebanest-e1a43.firebasestorage.app/locations/Tannourine.jpeg',
    description: 'Waterfalls, cedar reserve, quiet roads',
  },
];

function useLocationCounts() {
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    async function fetchCounts() {
      try {
        const listingsRef = collection(db, 'listings');
        const q = query(listingsRef, where('status', '==', 'published'));
        const snapshot = await getDocs(q);

        const locationCounts: Record<string, number> = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const city = (data.location?.city || '').toLowerCase().replace(/[-_\s]+/g, '');
          const area = (data.location?.area || '').toLowerCase().replace(/[-_\s]+/g, '');

          if (!city && !area) return;

          for (const loc of FEATURED_LOCATIONS_BASE) {
            const slug = loc.slug.replace(/[-_\s]+/g, '');
            if (
              (city && (city === slug || city.includes(slug) || slug.includes(city))) ||
              (area && (area === slug || area.includes(slug) || slug.includes(area)))
            ) {
              locationCounts[loc.slug] = (locationCounts[loc.slug] || 0) + 1;
              break; // each listing counted only once
            }
          }
        });

        setCounts(locationCounts);
      } catch (err) {
        console.error('Failed to fetch location counts:', err);
      }
    }

    fetchCounts();
  }, []);

  return counts;
}

export const FeaturedLocations: React.FC = () => {
  const counts = useLocationCounts();

  const locations: Location[] = FEATURED_LOCATIONS_BASE.map((loc) => ({
    ...loc,
    propertyCount: counts[loc.slug] || 0,
  }));

  return (
    <section className="py-16 bg-gray-50">
      <Container>
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Where to go
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Six places worth the drive. Coast, mountains, and the cities in
            between.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location) => (
            <LocationCard key={location.slug} location={location} />
          ))}
        </div>
      </Container>
    </section>
  );
};

interface LocationCardProps {
  location: Location;
}

const LocationCard: React.FC<LocationCardProps> = ({ location }) => {
  return (
    <Link
      to={`/search?location=${location.slug}`}
      className="group relative overflow-hidden rounded-2xl aspect-[4/3] block"
    >
      {/* Image */}
      <img
        src={location.image}
        alt={location.name}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        loading="lazy"
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-xl font-bold text-white mb-1">{location.name}</h3>
        <p className="text-white/80 text-sm mb-2">{location.description}</p>
        <p className="text-white/60 text-sm">
          {location.propertyCount} properties
        </p>
      </div>

      {/* Hover arrow */}
      <div className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <ArrowIcon className="w-5 h-5 text-white" />
      </div>
    </Link>
  );
};

const ArrowIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

export default FeaturedLocations;
